import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { OwnershipDocument } from "../../models/OwnershipDocument";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { getOwnershipReproDocumentTemplate } from "../../document-templates/OwnershipReproDocumentTemplate";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllProducts } from "../../services/ProductService";
import {
  getOneOwnershipDocument,
  createOwnershipDocument,
  updateOneOwnershipDocument,
} from "../../services/OwnershipDocumentService";
import "./OwnershipReproDocumentPage.scss";

export interface OwnershipReproDocumentPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const OwnershipReproDocumentPage = (
  p: OwnershipReproDocumentPageProps,
) => {
  const { buttonSaveStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const toast = useRef<any>(null);
  const downloadPdfDocument = useRef<any>(null);
  const printPdfDocument = useRef<any>(null);

  const [processing, setProcessing] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);

  const [newOwnershipDocument, setNewOwnershipDocument] =
    useState<OwnershipDocument>({
      id: id ?? "",
      warehouse: {} as Warehouse,
      date: new Date(),
      srps: 0,
      product: {} as Product,
      repromaterialProducts: [],
      repromaterialProductsJSON: "",
      mercantile: false,
      previousOwner: {} as Partner,
      newOwner: {} as Partner,
      note: "",
    });
  const [newProduct, setNewProduct] = useState<Product>({
    id: "",
    name: "",
    type: ProductType.RawMaterial,
    code: "",
    category: {} as ProductCategory,
    lot: "",
    amount: "",
    measuringUnit: "",
    germination: "",
    activeSubstance: "",
    expiryDate: undefined,
  });
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const productsDataTable = useMemo(() => {
    const list = newOwnershipDocument.repromaterialProducts ?? [];
    return list.map((p: any) => {
      const master = productsApi.find((x) => x.id == p?.id);
      return {
        id: master?.id ?? p?.id ?? "",
        type: ProductType.RawMaterial,
        name: master?.name ?? p?.name ?? "",
        code: master?.name ?? p?.code ?? "",
        category: master?.category ?? p?.category ?? ({} as ProductCategory),
        lot: p?.lot ?? "",
        amount: p?.amount ?? "",
        measuringUnit: master?.measuringUnit ?? p?.measuringUnit ?? "",
        germination: p?.germination ?? "",
        activeSubstance: p?.activeSubstance ?? "",
        expiryDate: p?.expiryDate ? new Date(p.expiryDate as any) : undefined,
      } as Product;
    });
  }, [newOwnershipDocument.repromaterialProducts, productsApi]);

  const [pdfData, setPdfData] = useState<any>(
    getOwnershipReproDocumentTemplate(newOwnershipDocument),
  );

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const safeParseProducts = (json?: string): Product[] => {
    try {
      const parsed = JSON.parse(json || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const refreshLookups = async () => {
    const [wResp, partnerResp, pcResp, pResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllProductCategories(),
      getAllProducts(),
    ]);

    [wResp, partnerResp, pcResp, pResp].forEach(ensureSuccess);

    setWarehousesApi(wResp?.data?.result ?? []);
    setPartnersApi(partnerResp?.data?.result ?? []);
    setProductCategoriesApi(pcResp?.data?.result ?? []);
    setProductsApi(pResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);
    refreshLookups()
      .catch((e: any) => {
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      })
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return;

    setProcessing(true);
    getOneOwnershipDocument(id)
      .then((resp) => {
        ensureSuccess(resp);

        const doc = resp?.data?.result;
        if (!doc) return;

        const normalized: any = { ...doc };
        normalized.warehouse = normalized.warehouse ?? ({} as Warehouse);
        normalized.previousOwner = normalized.previousOwner ?? ({} as Partner);
        normalized.newOwner = normalized.newOwner ?? ({} as Partner);
        if (normalized.date) normalized.date = new Date(normalized.date);

        const allProducts = safeParseProducts(
          normalized.repromaterialProductsJSON,
        );
        normalized.repromaterialProducts = allProducts;
        normalized.repromaterialProductsJSON =
          normalized.repromaterialProductsJSON ?? "[]";
        normalized.repromaterialProducts = safeParseProducts(
          normalized.repromaterialProductsJSON,
        );

        setNewOwnershipDocument(normalized);
        setUpdating(true);
      })
      .catch(() => {
        setUpdating(false);
      })
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const catId = newProduct.category?.id;

    if (catId === undefined) {
      setFilteredProducts(
        productsApi.filter((x) => x.type === ProductType.RawMaterial),
      );
    } else {
      setFilteredProducts(productsApi.filter((x) => x.category?.id == catId));
    }
  }, [newProduct.category?.id, productsApi]);

  const repromaterialsForPdf = useMemo(() => {
    const list = newOwnershipDocument.repromaterialProducts ?? [];
    return list.map((p: any) => {
      const master = productsApi.find((x) => x.id === p?.id);

      return {
        ...p,
        id: master?.id ?? p?.id ?? "",
        name: master?.name ?? p?.name ?? "",
        measuringUnit: master?.measuringUnit ?? p?.measuringUnit ?? "",
        code: master?.code ?? p?.code ?? "",
        category: master?.category ?? p?.category,
      };
    });
  }, [newOwnershipDocument.repromaterialProducts, productsApi]);

  // populate pdf when data changes
  useEffect(() => {
    const docForPdf = {
      ...newOwnershipDocument,
      repromaterialProducts: repromaterialsForPdf,
    };

    const newPdf = getOwnershipReproDocumentTemplate(docForPdf as any);
    setPdfData(newPdf);
  }, [newOwnershipDocument, repromaterialsForPdf]);

  const handleNewOwnershipDocument = (prop: string, value: any) => {
    setNewOwnershipDocument((newOwnershipDocument) => ({
      ...newOwnershipDocument,
      [prop]: value,
    }));
  };

  const handlePreviousOwnerChange = (prop: string, value: any) => {
    let copyOfNewOwnershipDocument = cloneDeep(newOwnershipDocument);
    copyOfNewOwnershipDocument.previousOwner = partnersApi.find(
      (item) => item.id == value,
    ) as Partner;
    setNewOwnershipDocument(copyOfNewOwnershipDocument);
  };

  const handleNewOwnerChange = (prop: string, value: any) => {
    let copyOfNewOwnershipDocument = cloneDeep(newOwnershipDocument);
    copyOfNewOwnershipDocument.newOwner = partnersApi.find(
      (item) => item.id == value,
    ) as Partner;
    setNewOwnershipDocument(copyOfNewOwnershipDocument);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewOwnershipDocument = cloneDeep(newOwnershipDocument);
    copyOfNewOwnershipDocument.warehouse = warehousesApi.find(
      (item) => item.id == value,
    ) as Warehouse;
    setNewOwnershipDocument(copyOfNewOwnershipDocument);
  };

  const handleNewProduct = (prop: string, value: any) => {
    setNewProduct((newProduct) => ({
      ...newProduct,
      [prop]: value,
    }));
  };

  const handleCategoryChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.category = productCategoriesApi.find(
      (category) => category.id === value,
    );
    setNewProduct(copyOfNewProduct);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.id = value;
    copyOfNewProduct.name = value;
    copyOfNewProduct.measuringUnit = filteredProducts.find(
      (product) => product.id === value,
    )?.measuringUnit;
    if (newProduct.category?.id === undefined) {
      copyOfNewProduct.category = filteredProducts.find(
        (product) => product.id === value,
      )?.category;
    }
    setNewProduct(copyOfNewProduct);
  };

  const addNewProduct = () => {
    if (newProduct.name === "" || newProduct.amount === "") {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Moraju biti popunjena polja za naziv i količinu proizvoda`),
        life: 3000,
      });
      return;
    }
    if ((newProduct.amount as string).indexOf(",") >= 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Neispravan unos količine. Decimalni broj treba uneti sa . a ne sa ,`,
        ),
        life: 3000,
      });
      return;
    }
    if (!/^-?\d+(\.\d+)?$/.test(newProduct.amount as string)) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Količina uneta u neispravnom formatu`),
        life: 3000,
      });
      return;
    }

    const productAdded = newOwnershipDocument.repromaterialProducts?.find(
      (item) => item.id == newProduct.id,
    );
    if (!productAdded) {
      //add product to allProductList
      let copyOfNewOwnershipDocument = cloneDeep(newOwnershipDocument);
      let copyOfNewProduct = cloneDeep(newProduct);
      copyOfNewOwnershipDocument.repromaterialProducts?.push(copyOfNewProduct);
      setNewOwnershipDocument(copyOfNewOwnershipDocument);
    } else {
      //update existing product
      let copyOfNewOwnershipDocument = cloneDeep(newOwnershipDocument);
      newOwnershipDocument.repromaterialProducts?.map((product, index) => {
        if (product.id === newProduct.id)
          //@ts-ignore
          copyOfNewOwnershipDocument.repromaterialProducts[index] =
            cloneDeep(newProduct);
      });
      setNewOwnershipDocument(copyOfNewOwnershipDocument);
    }
    setFilteredProducts(
      productsApi.filter((item) => item.type === ProductType.RawMaterial),
    );
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      category: {} as ProductCategory,
      lot: "",
      amount: "",
      measuringUnit: "",
      germination: "",
      activeSubstance: "",
      expiryDate: undefined,
    });
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = async () => {
    const copy = cloneDeep(newOwnershipDocument);
    copy.repromaterialProductsJSON = JSON.stringify(
      copy.repromaterialProducts ?? [],
    );

    setProcessing(true);
    try {
      if (updating) {
        const resp = await updateOneOwnershipDocument(copy.id, copy);
        ensureSuccess(resp);
      } else {
        const resp = await createOwnershipDocument(copy);
        ensureSuccess(resp);
      }

      toast.current?.show({
        severity: "success",
        detail: t("Uspešno popunjen vlasnički list"),
        life: 3000,
      });

      navigate(`/ownership-repro-documents`);
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          String(t("Greška pri snimanju")),
        life: 3500,
      });
    } finally {
      setProcessing(false);
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setNewOwnershipDocument({
      id: id ?? "",
      warehouse: {} as Warehouse,
      date: new Date(),
      netto: 0,
      srps: 0,
      product: {} as Product,
      mercantile: false,
      previousOwner: {} as Partner,
      newOwner: {} as Partner,
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      note: "",
    });
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const nameOfProduct = (rowData: any) => rowData?.name ?? "";

  const nameOfCategory = (rowData: any) => rowData?.category?.name ?? "";

  const editActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-pencil"
        onButtonClick={() => {
          setNewProduct(rowData);
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-file-text"
              style={{ fontSize: 16, marginRight: 6 }}
            ></i>
            <span className="header-title">
              {`${t("Vlasnički list repromaterijala broj")}:`}{" "}
              {newOwnershipDocument?.ownerDocNumberFormatted ??
                newOwnershipDocument.ownerDocId}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Svi vlasnički listovi")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/ownership-repro-documents`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
              disabled={
                !(
                  newOwnershipDocument?.ownerDocNumberFormatted ||
                  newOwnershipDocument.ownerDocId
                )
              }
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
              disabled={
                !(
                  newOwnershipDocument?.ownerDocNumberFormatted ||
                  newOwnershipDocument.ownerDocId
                )
              }
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              <DropdownInput
                id="warehouse"
                value={newOwnershipDocument.warehouse.id}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehousesApi.filter((item) => item.virtual !== true)}
                filter={false}
                labelText={`${t("Magacin")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DateInput
                id="date"
                value={newOwnershipDocument.date as Date}
                onValueChange={handleNewOwnershipDocument}
                labelText={`${t("Datum")} *`}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="previousOwner"
                value={newOwnershipDocument.previousOwner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handlePreviousOwnerChange}
                options={partnersApi}
                filter={true}
                labelText={`${t("Prethodni vlasnik")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="newOwner"
                value={newOwnershipDocument.newOwner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleNewOwnerChange}
                options={partnersApi}
                filter={true}
                labelText={`${t("Novi vlasnik")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <div className="divider-line">
                <Divider />
              </div>
              <>
                <DropdownInput
                  id="category"
                  value={newProduct.category?.id}
                  onValueChange={handleCategoryChange}
                  optionValue="id"
                  optionLabel="name"
                  options={productCategoriesApi.filter(
                    (x) => x.type == ProductType.RawMaterial,
                  )}
                  labelText={t("Tip proizvoda")}
                  placeholderText={t("Izaberite tip proizvoda")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
                <DropdownInput
                  id="name"
                  value={newProduct.id}
                  optionValue="id"
                  optionLabel="name"
                  onValueChange={handleProductChange}
                  options={filteredProducts}
                  filter={true}
                  labelText={t("Proizvod")}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
                <div className="repro-product-details">
                  <TextInput
                    id="amount"
                    value={newProduct.amount as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Količina")}
                    placeholderText="Unesite količinu"
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{
                      pattern: {
                        value: /\d+\.?\d*/,
                        message: "Please enter a number",
                      },
                    }}
                  />
                  <TextInput
                    id="measuringUnit"
                    value={newProduct.measuringUnit as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Jedinica mere")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={true}
                  />
                  <div className="add-product">
                    <ButtonInput
                      labelText={t("Dodaj proizvod")}
                      icon="bi bi-plus-square"
                      onButtonClick={addNewProduct}
                      addStyle={{
                        border: "none",
                        background: "rgba(21, 115, 71, 1)",
                        color: "#ffffff",
                        height: "2.6rem",
                      }}
                    />
                  </div>
                </div>
              </>
              <div className="data-table-container">
                <DataTable value={productsDataTable}>
                  <Column
                    body={nameOfProduct}
                    header={`${t(`Naziv proizvoda`)}:`}
                  ></Column>
                  <Column
                    body={nameOfCategory}
                    header={`${t("Tip proizvoda")}:`}
                  ></Column>
                  <Column field="amount" header={`${t("Količina")}:`}></Column>
                  <Column
                    field="measuringUnit"
                    header={`${t("M.J:")}`}
                  ></Column>
                  <Column
                    body={editActionBodyTemplate}
                    className="datatable-button-column"
                    header={t("Opcije")}
                  />
                </DataTable>
              </div>
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newOwnershipDocument.note as string}
                  onValueChange={handleNewOwnershipDocument}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              </div>
              <div></div>
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={handleSubmit(onSubmit, onError)}
                  addStyle={addButtonSaveStyle}
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                />
              </div>
            </div>
            <div className="documents-preview">
              <PdfPreview
                pdfDefinition={pdfData}
                downloadDocument={downloadPdfDocument}
                printDocument={printPdfDocument}
                documentName={`Vlasnički_list_${
                  newOwnershipDocument?.ownerDocNumberFormatted ??
                  newOwnershipDocument.ownerDocId ??
                  ""
                }`}
              />
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
